/**
 * Module-level state for the playlist asset picker's last browsed folder path.
 * Extracted to its own file so logout.ts can reset it without importing
 * the heavy AssetPickerPanel (which pulls in Uppy, dnd-kit, etc.).
 *
 * Always prefers the last-visited folder from the Assets page (persisted by
 * usePersistedPath in sessionStorage). This ensures cross-page folder context:
 * user browses /Folder1/Folder2 on Assets → opens Asset Picker in Playlists →
 * picker opens at /Folder1/Folder2. Module-level state is only a fallback
 * when sessionStorage is empty (e.g., user never visited Assets page).
 *
 * Returns full context { path, teamId?, shared? } so shared folders from other
 * teams query with the correct teamId (not the user's activeTeamId).
 */

import { PERSISTED_PATH_PREFIX } from '@/hooks/usePersistedPath';

export interface PickerPathContext {
  path: string;
  teamId?: string;
  /** When true, picker should enter "Shared with me" mode */
  shared?: boolean;
}

/** Assets page uses this special path value for "Shared with me" root */
const SHARE_WITH_ME_PATH = 'SHARE_WITH_ME';

let lastPickerContext: PickerPathContext = { path: '/' };

export function getPickerPathContext(): PickerPathContext {
  // Always prefer the Assets page's last-visited folder (sessionStorage)
  try {
    const stored = sessionStorage.getItem(`${PERSISTED_PATH_PREFIX}assets`);
    if (stored) {
      const parsed: Record<string, string> = JSON.parse(stored);
      if (!parsed.path || parsed.path === '/') return lastPickerContext;

      // Assets page at "Shared with me" root → picker should enter shared mode at root
      if (parsed.path === SHARE_WITH_ME_PATH) {
        return { path: '/', shared: true };
      }

      // Inside a shared folder → preserve full context
      if (parsed.shared) {
        return {
          path: parsed.path,
          teamId: parsed.teamId,
          shared: true,
        };
      }

      // Normal folder
      return { path: parsed.path, teamId: parsed.teamId };
    }
  } catch {
    // Invalid stored data — ignore
  }
  return lastPickerContext;
}

/** @deprecated Use getPickerPathContext() for full shared-folder support */
export function getPickerPath() {
  return getPickerPathContext().path;
}

export function setPickerPath(path: string) {
  lastPickerContext = { ...lastPickerContext, path };
}

/** Resets the picker path to root. Called on logout to prevent cross-account path leakage. */
export function resetPickerPath() {
  lastPickerContext = { path: '/' };
}
